import React, { useMemo, useState } from 'react';
import { ArrowRight, Calculator, Shield, Heart, ShieldAlert, Building2, Home, PawPrint, Car, Umbrella, Search, X, BoxIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { RECHNER } from '../lib/rechner';
import { RechnerDetailPage } from './RechnerDetailPage';
import { AnimatedGridPattern } from '../components/ui/AnimatedGridPattern';
import { Grid } from '../components/ui/GridPattern';
interface RechnerPageProps {
  onNavigate: (page: string) => void;
}
// Map categories to icons
const categoryIcons: Record<string, BoxIcon> = {
  Kranken: Heart,
  'Sach / Haftpflicht': ShieldAlert,
  Sach: Shield,
  Gewerbe: Building2,
  Immobilie: Home,
  Tier: PawPrint,
  KFZ: Car,
  Vorsorge: Umbrella
};
function groupByCategory(items: typeof RECHNER) {
  const map = new Map<string, typeof RECHNER>();
  for (const r of items) {
    const key = r.category;
    map.set(key, [...(map.get(key) ?? []), r]);
  }
  return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
}
export function RechnerPage({
  onNavigate
}: RechnerPageProps) {
  const [selectedRechner, setSelectedRechner] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Filter calculators based on search query
  const filteredRechner = useMemo(() => {
    if (!searchQuery.trim()) return RECHNER;
    const query = searchQuery.toLowerCase();
    return RECHNER.filter(r => r.label.toLowerCase().includes(query) || r.category.toLowerCase().includes(query));
  }, [searchQuery]);
  const groups = groupByCategory(filteredRechner);
  const totalResults = filteredRechner.length;
  // If a calculator is selected, show the detail view
  if (selectedRechner) {
    return <RechnerDetailPage rechnerId={selectedRechner} onNavigate={onNavigate} onBack={() => setSelectedRechner(null)} data-id="element-882" />;
  }
  // Otherwise show the overview
  return <div className="min-h-screen bg-sp-light dark:bg-slate-950 transition-colors duration-300" data-id="element-883">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-sky-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-blue-950/20 py-16 lg:py-24 border-b border-slate-200 dark:border-slate-800" data-id="element-884">
        <AnimatedGridPattern numSquares={30} maxOpacity={0.15} duration={3} repeatDelay={1} className="[mask-image:radial-gradient(600px_circle_at_center,white,transparent)] inset-x-0 inset-y-[-30%] h-[200%] skew-y-12" data-id="element-885" />

        <div className="container mx-auto px-4 text-center relative z-10" data-id="element-886">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 dark:bg-blue-950/30 px-3 py-1 text-xs font-medium text-blue-700 dark:text-blue-400 border border-blue-100 dark:border-blue-900 mb-6" data-id="element-887">
            <Calculator className="h-3.5 w-3.5" data-id="element-888" />
            <span data-id="element-889">Online Vergleichsrechner</span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 dark:text-white mb-6 tracking-tight" data-id="element-890">
            Versicherungsrechner
          </h1>
          <p className="text-lg sm:text-xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto leading-relaxed mb-10" data-id="element-891">
            Wähle den passenden Rechner, vergleiche Tarife und finde das beste
            Angebot für deine Bedürfnisse.
          </p>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto relative" data-id="element-892">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-893">
              <Search className="h-5 w-5 text-slate-400" data-id="element-894" />
            </div>
            <input type="text" placeholder="Rechner suchen (z.B. Hausrat, KFZ, Kranken...)" className="w-full pl-12 pr-12 py-4 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-base" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-895" />
            {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors" data-id="element-896">
                <X className="h-5 w-5" data-id="element-897" />
              </button>}
          </div>

          {/* App Hint */}
          <div className="mt-6 inline-flex items-center gap-2 bg-blue-50 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-900/50 rounded-xl px-4 py-2.5 text-sm text-blue-700 dark:text-blue-300" data-id="element-898">
            <Shield className="h-4 w-4 flex-shrink-0" data-id="element-899" />
            <span data-id="element-900">
              <strong data-id="element-901">Tipp:</strong> Für bessere Darstellung der
              Vergleichsrechner gerne direkt{' '}
              <button onClick={() => onNavigate('app')} className="font-semibold underline underline-offset-2 hover:text-blue-800 dark:hover:text-blue-200 transition-colors" data-id="element-902">
                die App benutzen
              </button>
            </span>
          </div>

          {/* Search Results Count */}
          {searchQuery && <p className="mt-4 text-sm text-slate-500 dark:text-slate-400" data-id="element-903">
              {totalResults} {totalResults === 1 ? 'Rechner' : 'Rechner'}{' '}
              gefunden
            </p>}
        </div>
      </div>

      <div className="container mx-auto px-4 py-16 lg:py-24 max-w-5xl" data-id="element-904">
        {/* Calculator Groups */}
        {groups.length > 0 ? <div className="space-y-16" data-id="element-905">
            {groups.map(([category, items]) => {
          const CategoryIcon = categoryIcons[category] || Calculator;
          return <section key={category} className="text-center" data-id="element-906">
                  {/* Category Header */}
                  <div className="mb-8 flex items-center justify-center gap-4" data-id="element-907">
                    <div className="h-8 w-1.5 bg-blue-600 rounded-full" data-id="element-908"></div>
                    <h2 className="text-2xl font-bold text-slate-900 dark:text-white" data-id="element-909">
                      {category}
                    </h2>
                    <Badge variant="secondary" className="bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-100 dark:border-blue-800" data-id="element-910">
                      {items.length}
                    </Badge>
                  </div>

                  {/* Calculator Cards Grid - Centered */}
                  <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 max-w-4xl mx-auto" data-id="element-911">
                    {items.map(r => <div key={r.id} className="relative bg-gradient-to-b dark:from-neutral-900 from-neutral-100 dark:to-neutral-950 to-white p-5 rounded-2xl overflow-hidden border border-slate-200/50 dark:border-slate-800/50 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group cursor-pointer text-left" onClick={() => setSelectedRechner(r.id)} data-id="element-912">
                        <Grid size={20} data-id="element-913" />

                        <div className="relative z-20 flex flex-col h-full" data-id="element-914">
                          <div className="flex items-center gap-3 mb-3" data-id="element-915">
                            <div className="h-10 w-10 rounded-xl bg-white dark:bg-slate-800 shadow-sm flex items-center justify-center border border-slate-100 dark:border-slate-700 group-hover:scale-110 transition-transform duration-300 flex-shrink-0" data-id="element-916">
                              <CategoryIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" data-id="element-917" />
                            </div>
                            <h3 className="text-base font-bold text-neutral-800 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" data-id="element-918">
                              {r.label}
                            </h3>
                          </div>

                          <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4 flex-grow" data-id="element-919">
                            Tarife vergleichen & sparen
                          </p>

                          <Button size="sm" variant="outline" className="w-full rounded-xl text-sm" onClick={e => {
                    e.stopPropagation();
                    setSelectedRechner(r.id);
                  }} data-id="element-920">
                            Öffnen
                            <ArrowRight className="ml-2 h-4 w-4" data-id="element-921" />
                          </Button>
                        </div>
                      </div>)}
                  </div>
                </section>;
        })}
          </div> /* No Results */ : <div className="text-center py-16" data-id="element-922">
            <div className="inline-flex h-16 w-16 bg-slate-100 dark:bg-slate-800 rounded-2xl items-center justify-center mb-6" data-id="element-923">
              <Search className="h-8 w-8 text-slate-400" data-id="element-924" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2" data-id="element-925">
              Keine Rechner gefunden
            </h3>
            <p className="text-slate-500 dark:text-slate-400 mb-6" data-id="element-926">
              Versuche einen anderen Suchbegriff oder{' '}
              <button onClick={() => setSearchQuery('')} className="text-blue-600 dark:text-blue-400 hover:underline" data-id="element-927">
                zeige alle Rechner
              </button>
            </p>
          </div>}

        {/* CTA Section */}
        <div className="relative bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-8 md:p-12 text-white shadow-xl overflow-hidden text-center mt-20" data-id="element-928">
          <Grid size={30} data-id="element-929" />
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-32 -mt-32" data-id="element-930"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl -ml-32 -mb-32" data-id="element-931"></div>

          <div className="relative z-10 max-w-2xl mx-auto" data-id="element-932">
            <div className="inline-flex h-16 w-16 bg-white/20 rounded-2xl items-center justify-center backdrop-blur-sm mb-6" data-id="element-933">
              <Shield className="h-8 w-8 text-white" data-id="element-934" />
            </div>
            <h3 className="text-2xl md:text-3xl font-bold mb-4" data-id="element-935">
              Nicht sicher, welcher Rechner passt?
            </h3>
            <p className="text-blue-100 text-lg mb-8 leading-relaxed" data-id="element-936">
              Unsere Experten helfen dir gerne weiter und finden gemeinsam mit
              dir den optimalen Versicherungsschutz.
            </p>
            <Button size="lg" variant="white" className="rounded-full px-8 hover:scale-105 transition-all duration-300" onClick={() => onNavigate('beratung')} data-id="element-937">
              Kostenlose Beratung anfragen
              <ArrowRight className="ml-2 h-5 w-5" data-id="element-938" />
            </Button>
          </div>
        </div>
      </div>
    </div>;
}